import {
  csvAmount,
  csvDocument,
  csvField,
  cardPayOnlineLabel,
  csvNumber,
  formatCount,
  parseMoneyToCents,
  qrPayLabel,
  rateOf,
  tt,
} from '@agos/shared'

/**
 * `/reports` — *Manažér – správa firmy*. Seven tabs over twelve endpoints.
 *
 * Legacy dropped three of its own tabs into `/analytics` (sales, products, customers) and kept the
 * financial and inventory ones, which is the split this port keeps.
 *
 * What it gets wrong, all measured against the restored dumps:
 *
 * - **FB-119** two profits for the same period, on the same card, 5 002,80 € apart
 * - **FB-120** a yearly margin of 98,6 %, because four supplier invoices are the whole cost of the year
 * - **FB-121** a stock valuation and a waste cost computable for 3 of 135 items
 * - **FB-122** consumption summed across two units, putting 19 tonnes of beef at the top of the month
 * - **FB-123** six CSV exports that quote every number, so no amount in them can be summed
 */

// ─── the cost of running the place, and which parts of it are counted ────────

export interface DetailedCosts {
  invoices?: { count?: number; gross?: number | string; net?: number | string; vat?: number | string }
  stockReceipts?: { count?: number; total?: number | string }
  wagesAndPayouts?: { total?: number | string }
  total?: number | string
}

export interface DetailedRevenue {
  total?: number | string
  orderCount?: number
  byChannel?: { delivery?: number | string; restaurant?: number | string; pickup?: number | string }
  byPayment?: {
    cash?: number | string
    cardTerminal?: number | string
    cardCourier?: number | string
    cardOnline?: number | string
    /** §9.741, BACKEND.md §10A.390 - a QR payment taken at the door. */
    qrPay?: number | string
  }
}

export interface DetailedMonth {
  month?: string
  revenue?: number | string
  cost?: number | string
  profit?: number | string
}

export interface DetailedSummary {
  from?: string
  to?: string
  revenue?: DetailedRevenue
  costs?: DetailedCosts
  perSupplier?: { supplierId?: number; name?: string; invoiceCount?: number; gross?: number | string; net?: number | string }[]
  profit?: number | string
  marginPct?: number | string
  monthly?: DetailedMonth[]
}

export function cents(value: number | string | null | undefined): number {
  return parseMoneyToCents(value)
}

export function num(value: number | string | null | undefined): number {
  return rateOf(value)
}

/** `byPayment`'s own keys: the financial summary answers them camelCased, not as the column's values. */
const PAYMENT_LABELS: Record<string, string> = {
  cash: tt('Hotovosť'),
  cardTerminal: tt('Karta terminál'),
  cardCourier: tt('Karta kuriér'),
  // §9.767 - the online card's one name; it said *Karta online*.
  cardOnline: cardPayOnlineLabel(),
  qrPay: qrPayLabel(),
}

export interface PaymentBar {
  key: string
  label: string
  valueCents: number
}

/**
 * *Tržby podľa platby* - one bar a key of `byPayment`, in the order the server sends them.
 *
 * **§9.741, BACKEND.md §10A.390: `qrPay` is the fifth key**, so the bars add up to the revenue total again - before
 * §10A.390 a door QR payment was in that total and in no bar. **§9.767, owner O129: *CardPay online* and *QR Pay*
 * are always there, QR Pay right after CardPay online**, a nought included (§9.741 hid QR Pay at nought, and the owner
 * read the missing bar as QR Pay not being there); a key an older server does not send is drawn as a nought. An
 * answer with no figures at all is still no bars, rather than two noughts claiming a period was counted.
 *
 * A key the map does not know keeps its own name, as the screen always did, so a method the server adds
 * later is drawn rather than lost.
 */
export function paymentBars(byPayment: DetailedRevenue['byPayment']): PaymentBar[] {
  const bars = Object.entries(byPayment ?? {}).map(([key, value]) => ({
    key,
    label: PAYMENT_LABELS[key] ?? key,
    valueCents: cents(value),
  }))
  if (bars.length === 0) return bars
  const nought = (key: string): PaymentBar => ({ key, label: PAYMENT_LABELS[key] ?? key, valueCents: 0 })
  const online = bars.find((bar) => bar.key === 'cardOnline') ?? nought('cardOnline')
  const qrPay = bars.find((bar) => bar.key === 'qrPay') ?? nought('qrPay')
  const rest = bars.filter((bar) => bar.key !== 'cardOnline' && bar.key !== 'qrPay')
  const onlineAt = bars.findIndex((bar) => bar.key === 'cardOnline')
  // CardPay online where the server put it - after the other cards - and QR Pay straight after it.
  const before = onlineAt === -1 ? rest : rest.filter((bar) => bars.indexOf(bar) < onlineAt)
  const after = onlineAt === -1 ? [] : rest.filter((bar) => bars.indexOf(bar) > onlineAt)
  return [...before, online, qrPay, ...after]
}

export interface CostBreakdown {
  invoiceCents: number
  receiptCents: number
  wagesCents: number
  /** What the server calls the total. */
  statedCents: number
  /** What the three parts add up to. */
  partsCents: number
  addsUp: boolean
}

export function costBreakdown(costs: DetailedCosts | undefined): CostBreakdown {
  const invoiceCents = cents(costs?.invoices?.gross)
  const receiptCents = cents(costs?.stockReceipts?.total)
  const wagesCents = cents(costs?.wagesAndPayouts?.total)
  const partsCents = invoiceCents + receiptCents + wagesCents
  return {
    invoiceCents,
    receiptCents,
    wagesCents,
    statedCents: cents(costs?.total),
    partsCents,
    addsUp: partsCents === cents(costs?.total),
  }
}

export interface ProfitReconciliation {
  revenueCents: number
  /** The header's figure: revenue less invoices, stock receipts and wages. */
  headlineProfitCents: number
  headlineCostCents: number
  /** The monthly rows' figure over the same span: revenue less **supplier invoices only**. */
  monthlyProfitCents: number
  monthlyCostCents: number
  /** What the monthly rows leave out. Zero when there is nothing to leave out. */
  gapCents: number
  disagrees: boolean
  /** The parts of the cost the monthly rows ignore, named so the note can list them. */
  missing: readonly ('receipts' | 'wages')[]
}

/**
 * **FB-119.** Two profits for one period, inches apart on one card.
 *
 * `/financial-summary/detailed?from=2026-07-01&to=2026-07-30` answers, for July:
 *
 * | | Revenue | Cost | Profit |
 * |---|---|---|---|
 * | header | 44 807,65 | **6 202,80** — invoices 1 200,00 + receipts 18,00 + wages 4 984,80 | **38 604,85** |
 * | `monthly[0]` | 44 807,65 | **1 200,00** — invoices only | **43 607,65** |
 *
 * Both are internally consistent; the monthly series simply counts a narrower cost. Legacy renders the
 * header as KPI cards and the series as a table below them, so on a single-month range the same period
 * shows two profits **5 002,80 € apart** with nothing to say why.
 */
export function reconcileProfit(summary: DetailedSummary): ProfitReconciliation {
  const revenueCents = cents(summary.revenue?.total)
  const breakdown = costBreakdown(summary.costs)
  const monthly = summary.monthly ?? []
  const monthlyCostCents = monthly.reduce((sum, month) => sum + cents(month.cost), 0)
  const monthlyProfitCents = monthly.reduce((sum, month) => sum + cents(month.profit), 0)
  const headlineProfitCents = cents(summary.profit)

  const missing: ('receipts' | 'wages')[] = []
  if (breakdown.receiptCents > 0) missing.push('receipts')
  if (breakdown.wagesCents > 0) missing.push('wages')

  return {
    revenueCents,
    headlineProfitCents,
    headlineCostCents: breakdown.statedCents,
    monthlyProfitCents,
    monthlyCostCents,
    gapCents: monthlyProfitCents - headlineProfitCents,
    // Only when the monthly series covers the same span; a multi-month range sums to the same revenue.
    disagrees:
      monthly.length > 0 &&
      monthlyProfitCents !== headlineProfitCents &&
      cents(monthly.reduce((sum, month) => sum + cents(month.revenue), 0) / 100) === revenueCents,
    missing,
  }
}

/** A margin from figures already in cents — never from a re-derived percentage. */
export function marginPercent(revenueCents: number, profitCents: number): number | null {
  if (revenueCents === 0) return null
  return (profitCents / revenueCents) * 100
}

// ─── the year ────────────────────────────────────────────────────────────────

export interface YearRow {
  year?: number
  revenue?: number | string
  orderCount?: number
  revenueDelivery?: number | string
  revenueRestaurant?: number | string
  revenuePickup?: number | string
  invoiceCount?: number
  costGross?: number | string
  costNet?: number | string
  profit?: number | string
  marginPct?: number | string
}

export interface YearlySummary {
  years?: YearRow[]
  monthly?: Record<string, { month?: number; revenue?: number | string }[]>
}

export interface Year {
  year: number
  revenueCents: number
  orders: number
  deliveryCents: number
  restaurantCents: number
  pickupCents: number
  invoiceCount: number
  costCents: number
  profitCents: number
  marginPercent: number | null
  /** Whether the three channels add up to the revenue. */
  channelsAgree: boolean
  /** **FB-120.** Whether the "cost" is only supplier invoices, which makes the margin meaningless. */
  costIsInvoicesOnly: boolean
}

/**
 * **FB-120.** A margin of 98,6 %, from four invoices.
 *
 * `/financial-summary/yearly` reports `costGross` as **the supplier invoices and nothing else**. For
 * 2026 on the restored data that is **2 921,54 € against 208 749,55 € of revenue** — four invoices —
 * giving `marginPct: 98.6`. No wages, no stock receipts, no waste, no rent. `/financial-summary/detailed`
 * over the same year would count 4 984,80 € of wages in July alone.
 *
 * Legacy renders that 98,6 % as a headline figure beside the revenue, which is the one number on the
 * page somebody would quote at a bank.
 *
 * A restaurant's food cost alone is a third of revenue, so any margin above about 80 % is arithmetic
 * rather than trade. The threshold is deliberately loose: the point is not to guess the real margin, it
 * is to refuse to present this one as if it were the real margin.
 */
export const IMPLAUSIBLE_MARGIN_PERCENT = 80

export function yearRows(summary: YearlySummary): Year[] {
  return (summary.years ?? [])
    .map((row) => {
      const revenueCents = cents(row.revenue)
      const deliveryCents = cents(row.revenueDelivery)
      const restaurantCents = cents(row.revenueRestaurant)
      const pickupCents = cents(row.revenuePickup)
      const costCents = cents(row.costGross)
      const profitCents = cents(row.profit)
      const margin = marginPercent(revenueCents, profitCents)
      return {
        year: row.year ?? 0,
        revenueCents,
        orders: row.orderCount ?? 0,
        deliveryCents,
        restaurantCents,
        pickupCents,
        invoiceCount: row.invoiceCount ?? 0,
        costCents,
        profitCents,
        marginPercent: margin,
        channelsAgree: deliveryCents + restaurantCents + pickupCents === revenueCents,
        costIsInvoicesOnly: margin !== null && margin > IMPLAUSIBLE_MARGIN_PERCENT,
      }
    })
    .sort((left, right) => right.year - left.year)
}

export function monthlyRevenue(
  summary: YearlySummary,
  year: number,
): { month: number; revenueCents: number }[] {
  const rows = summary.monthly?.[String(year)] ?? []
  return rows
    .map((row) => ({ month: row.month ?? 0, revenueCents: cents(row.revenue) }))
    .sort((left, right) => left.month - right.month)
}

// ─── the stock ───────────────────────────────────────────────────────────────

export interface StockValuation {
  totalValuation?: number | string
  totalItems?: number
  itemsWithoutCost?: number
  perCategory?: { category?: string; itemCount?: number; valuation?: number | string; totalQty?: number | string }[]
}

export interface Valuation {
  totalCents: number
  items: number
  withoutCost: number
  priced: number
  /** What share of the stock the valuation actually covers. `null` with no items. */
  coveragePercent: number | null
  categories: { category: string; items: number; valuationCents: number; quantity: number }[]
}

/**
 * **FB-121, first half.** A stock valued at 28,70 €.
 *
 * `/reports/stock-valuation` answers `totalValuation: 28.70`, `totalItems: 135`,
 * `itemsWithoutCost: 132` — so the figure covers **three items**, all of them the same flour, and
 * legacy prints it as *Hodnota skladu* with the 132 in a card beside it as though the two were peers.
 * A valuation over 2 % of the stock is not a valuation; it is the price of some flour.
 */
export function valuation(response: StockValuation | undefined): Valuation {
  const items = response?.totalItems ?? 0
  const withoutCost = response?.itemsWithoutCost ?? 0
  const priced = Math.max(0, items - withoutCost)
  return {
    totalCents: cents(response?.totalValuation),
    items,
    withoutCost,
    priced,
    coveragePercent: items === 0 ? null : (priced / items) * 100,
    categories: (response?.perCategory ?? [])
      .map((row) => ({
        category: row.category ?? '',
        items: row.itemCount ?? 0,
        valuationCents: cents(row.valuation),
        quantity: num(row.totalQty),
      }))
      .sort((left, right) => right.valuationCents - left.valuationCents),
  }
}

export interface Mover {
  id?: number
  name?: string
  category?: string | null
  unit?: string
  currentQty?: number | string
  costPrice?: number | string | null
  qtyOut?: number | string
  qtyIn?: number | string
  lastMovementAt?: string | null
}

export interface StockTurnover {
  days?: number
  fastMovers?: Mover[]
  deadStock?: Mover[]
}

/**
 * **FB-122.** The consumption figures are summed across two units.
 *
 * `stock_movements.qty` for one item holds both scales. For `Hovädzie mäso 130g`, whose unit is `kg`:
 *
 * | Quantity per movement | Rows |
 * |---|---|
 * | `0.130` | 1 095 |
 * | `0.080` | 499 |
 * | `0.260` | 197 |
 * | **`260.000`** | **188** |
 *
 * The 260-gram portion is recorded both as `0.260` and as `260.000` — the same portion, a factor of a
 * thousand apart. **23 of the 79 items measured in kg or l have movements at both scales.**
 *
 * Over the last thirty days that item's outflow sums to **19 191,68 kg**, of which the sub-100 rows
 * account for **211,68** and the rest for 18 980,00. So the reported figure is **90,7× the plausible
 * one** — 211,68 kg of beef at 130 g a burger is about 1 630 burgers in a month, which is the right
 * order of magnitude for this restaurant. Nineteen tonnes is not.
 *
 * Legacy ranks *Najrýchlejšie obracajúce sa* by this number, so the top of the list is whichever items
 * happen to have gram-scale rows. Nothing on this side can repair the data; what it can do is refuse to
 * present the ranking as a fact, and say which rows are the reason.
 */
export const IMPLAUSIBLE_STOCK_MULTIPLE = 1000

export interface Consumption {
  id: number
  name: string
  category: string | null
  unit: string
  outQuantity: number
  inQuantity: number
  currentQuantity: number
  lastMovement: string | null
  /**
   * True when the outflow is impossible against what the item is measured in.
   *
   * A kilogram item cannot lose thousands of kilograms a month from a kitchen this size, and the
   * signature of the mixed scale is exactly that: an outflow more than a thousand times the stock it
   * is held in, on an item priced per kilo.
   */
  scaleLooksMixed: boolean
}

export function consumption(movers: Mover[]): Consumption[] {
  return movers
    .map((mover) => {
      const unit = mover.unit ?? ''
      const outQuantity = num(mover.qtyOut)
      return {
        id: mover.id ?? 0,
        name: mover.name ?? '',
        category: mover.category ?? null,
        unit,
        outQuantity,
        inQuantity: num(mover.qtyIn),
        currentQuantity: num(mover.currentQty),
        lastMovement: mover.lastMovementAt ?? null,
        scaleLooksMixed: (unit === 'kg' || unit === 'l') && outQuantity >= IMPLAUSIBLE_STOCK_MULTIPLE,
      }
    })
    .sort((left, right) => right.outQuantity - left.outQuantity)
}

export function mixedScaleCount(rows: Consumption[]): number {
  return rows.filter((row) => row.scaleLooksMixed).length
}

export interface LowStockRow {
  id?: number
  name?: string
  category?: string | null
  unit?: string
  qty?: number | string
  minQty?: number | string
  deficit?: number | string
  defaultSupplierId?: number | null
  supplierName?: string | null
}

export interface Shortfall {
  id: number
  name: string
  category: string | null
  unit: string
  quantity: number
  minimum: number
  deficit: number
  supplier: string | null
  hasSupplier: boolean
}

/** Biggest shortfall first, and an item nobody can be asked to deliver is worth counting. */
export function shortfalls(rows: LowStockRow[]): Shortfall[] {
  return rows
    .map((row) => ({
      id: row.id ?? 0,
      name: row.name ?? '',
      category: row.category ?? null,
      unit: row.unit ?? '',
      quantity: num(row.qty),
      minimum: num(row.minQty),
      deficit: num(row.deficit),
      supplier: row.supplierName ?? null,
      hasSupplier: (row.supplierName ?? '') !== '',
    }))
    .sort((left, right) => right.deficit - left.deficit)
}

export function withoutSupplier(rows: Shortfall[]): number {
  return rows.filter((row) => !row.hasSupplier).length
}

// ─── the waste ───────────────────────────────────────────────────────────────

export interface WasteReport {
  days?: number
  summary?: { totalCost?: number | string; totalRecords?: number; distinctItems?: number }
  byItem?: { id?: number; name?: string; unit?: string; qty?: number | string; cost?: number | string; records?: number }[]
  byReason?: { reason?: string; cost?: number | string; records?: number }[]
  byDay?: { day?: string; cost?: number | string; records?: number }[]
}

export interface WasteItem {
  id: number
  name: string
  unit: string
  quantity: number
  costCents: number
  records: number
  /** A quantity written off that the stock could not have held. Same signature as FB-122. */
  quantityLooksWrong: boolean
  /** The cost is nought because the item has no price, not because the loss was free. */
  costUnknown: boolean
}

export interface WasteSummary {
  costCents: number
  records: number
  items: number
  /** How many of the written-off items had no price, so contributed nothing to the cost. */
  unpricedItems: number
  /** True when the reported loss exceeds the whole stock it was taken from. */
  exceedsValuation: boolean
  /** By how many times, for the note. `null` when there is nothing to compare against. */
  valuationMultiple: number | null
}

/**
 * **FB-121, second half.** A month's waste worth more than the entire stock.
 *
 * `/reports/waste?days=30` answers **41 995,80 €** across three records. The stock those losses came
 * out of is valued at **28,70 €**, so the report claims a loss **1 463 times** the whole inventory.
 *
 * The reason is the same three priced items. Four waste movements exist in the database:
 *
 * | Item | Quantity | `cost_price` | Contributes |
 * |---|---|---|---|
 * | Jahodový džem — *Po expirácii*, 6 May | 1 kg | **none** | **0,00 €** |
 * | Golden Múka T650 × 3 | 9 999 kg each | 1,40 € | 41 995,80 € |
 *
 * So the one genuine production write-off is invisible in the cost, and the figure on screen is three
 * rows of nine thousand kilos of flour. Legacy prints the 41 995,80 € as its headline KPI.
 *
 * Nothing here can fix the data. What it can do is refuse to print an impossible number as a fact:
 * a loss greater than the stock is arithmetic, not waste, and the page says which rows and why.
 */
export function wasteItems(report: WasteReport | undefined): WasteItem[] {
  return (report?.byItem ?? [])
    .map((row) => {
      const unit = row.unit ?? ''
      const quantity = num(row.qty)
      return {
        id: row.id ?? 0,
        name: row.name ?? '',
        unit,
        quantity,
        costCents: cents(row.cost),
        records: row.records ?? 0,
        quantityLooksWrong:
          (unit === 'kg' || unit === 'l') && quantity >= IMPLAUSIBLE_STOCK_MULTIPLE,
        costUnknown: cents(row.cost) === 0 && quantity > 0,
      }
    })
    .sort((left, right) => right.costCents - left.costCents || right.quantity - left.quantity)
}

export function wasteSummary(
  report: WasteReport | undefined,
  stockValuationCents: number,
): WasteSummary {
  const items = wasteItems(report)
  const costCents = cents(report?.summary?.totalCost)
  return {
    costCents,
    records: report?.summary?.totalRecords ?? 0,
    items: report?.summary?.distinctItems ?? items.length,
    unpricedItems: items.filter((item) => item.costUnknown).length,
    exceedsValuation: stockValuationCents > 0 && costCents > stockValuationCents,
    valuationMultiple: stockValuationCents === 0 ? null : costCents / stockValuationCents,
  }
}

export const WASTE_REASONS: Record<string, string> = {
  expired: tt('Po expirácii'),
  spoiled: tt('Pokazené'),
  damaged: tt('Poškodené'),
  spilled: tt('Rozliate'),
  test: tt('Testovanie'),
  other: tt('Iné'),
}

export function reasonLabel(reason: string | undefined): string {
  return WASTE_REASONS[reason ?? ''] ?? (reason ?? '')
}

// ─── VAT on the way in ───────────────────────────────────────────────────────

export interface VatReport {
  from?: string
  to?: string
  perRate?: {
    vatRate?: number | string
    totalNet?: number | string
    totalVat?: number | string
    totalGross?: number | string
    invoiceCount?: number
    lineCount?: number
  }[]
  invoices?: {
    id?: number
    invoiceNumber?: string
    issueDate?: string
    dueDate?: string | null
    paidAt?: string | null
    totalNet?: number | string
    totalVat?: number | string
    totalGross?: number | string
    status?: string
    supplierName?: string
  }[]
}

export interface VatBand {
  rate: number
  netCents: number
  vatCents: number
  grossCents: number
  invoices: number
  lines: number
  /** Whether net + VAT is the stated gross. A tax return is not the place to find out it is not. */
  addsUp: boolean
}

export function vatBands(report: VatReport | undefined): VatBand[] {
  return (report?.perRate ?? [])
    .map((row) => {
      const netCents = cents(row.totalNet)
      const vatCents = cents(row.totalVat)
      const grossCents = cents(row.totalGross)
      return {
        rate: num(row.vatRate),
        netCents,
        vatCents,
        grossCents,
        invoices: row.invoiceCount ?? 0,
        lines: row.lineCount ?? 0,
        addsUp: netCents + vatCents === grossCents,
      }
    })
    .sort((left, right) => right.rate - left.rate)
}

export interface VatTotals {
  netCents: number
  vatCents: number
  grossCents: number
  /** Every band adds up, so the total does too. */
  addsUp: boolean
}

export function vatTotals(bands: VatBand[]): VatTotals {
  return {
    netCents: bands.reduce((sum, band) => sum + band.netCents, 0),
    vatCents: bands.reduce((sum, band) => sum + band.vatCents, 0),
    grossCents: bands.reduce((sum, band) => sum + band.grossCents, 0),
    addsUp: bands.every((band) => band.addsUp),
  }
}

/**
 * Whether an invoice in the deductible-VAT report is one the business has actually received.
 *
 * A `draft` invoice has not been confirmed, so its VAT is not deductible yet. The endpoint returns the
 * status and legacy renders the row without it — on this data the single July invoice is a draft, which
 * means the whole period's input VAT of 191,60 € is provisional and the report does not say so.
 */
export function vatRowIsProvisional(status: string | undefined): boolean {
  return (status ?? '') === 'draft'
}

// ─── the wallets ─────────────────────────────────────────────────────────────

export interface WalletRow {
  id?: number
  name?: string
  systemRole?: string
  isExternal?: boolean
  walletTotal?: number | string
  cashTotal?: number | string
  cardTerminalTotal?: number | string
  cardCourierTotal?: number | string
  cardOnlineTotal?: number | string
  /** §9.741, BACKEND.md §10A.390 - a door QR payment, which went to the bank rather than to this person. */
  qrPayTotal?: number | string
  orderCount?: number
}

export interface Wallet {
  id: number
  name: string
  role: string
  isExternal: boolean
  walletCents: number
  cashCents: number
  cardTerminalCents: number
  cardCourierCents: number
  cardOnlineCents: number
  qrPayCents: number
  orders: number
  /** Whether the parts add to the stated wallet total. */
  addsUp: boolean
  partsCents: number
}

/**
 * The wallet rows, and whether each one's parts add up.
 *
 * `walletTotal` is what the employee owes; the four card and cash figures are what it is made of. They
 * are separate columns in the payload and legacy renders both without ever comparing them — which
 * matters here more than most places, because this is the number somebody hands over in cash.
 *
 * **And `qrPayTotal` is the fifth part since §10A.390** (§9.741): a door QR payment is in `walletTotal`
 * and in none of the four, so a sum without it would mark that person's wallet as not adding up.
 */
export function wallets(rows: WalletRow[]): Wallet[] {
  return rows
    .map((row) => {
      const cashCents = cents(row.cashTotal)
      const cardTerminalCents = cents(row.cardTerminalTotal)
      const cardCourierCents = cents(row.cardCourierTotal)
      const cardOnlineCents = cents(row.cardOnlineTotal)
      const qrPayCents = cents(row.qrPayTotal)
      const partsCents = cashCents + cardTerminalCents + cardCourierCents + cardOnlineCents + qrPayCents
      const walletCents = cents(row.walletTotal)
      return {
        id: row.id ?? 0,
        name: row.name ?? '',
        role: row.systemRole ?? '',
        isExternal: row.isExternal === true,
        walletCents,
        cashCents,
        cardTerminalCents,
        cardCourierCents,
        cardOnlineCents,
        qrPayCents,
        orders: row.orderCount ?? 0,
        partsCents,
        addsUp: partsCents === walletCents,
      }
    })
    .filter((wallet) => wallet.orders > 0 || wallet.walletCents !== 0)
    .sort((left, right) => right.walletCents - left.walletCents)
}

export interface WalletTotals {
  walletCents: number
  cashCents: number
  cardCents: number
  /** §9.767 - the online card's own share of `cardCents`, for its tile beside QR Pay's. */
  cardOnlineCents: number
  /**
   * §9.741 - kept apart from `cardCents`: QR Pay is not a card, and the server's own `cardTotal` stopped
   * counting it as one (§10A.390). Drawn always since §9.767, beside CardPay online.
   */
  qrPayCents: number
  orders: number
  people: number
  /** How many rows do not add up. Legacy never checks. */
  disagreeing: number
}

export function walletTotals(rows: Wallet[]): WalletTotals {
  return {
    walletCents: rows.reduce((sum, row) => sum + row.walletCents, 0),
    cashCents: rows.reduce((sum, row) => sum + row.cashCents, 0),
    cardCents: rows.reduce(
      (sum, row) => sum + row.cardTerminalCents + row.cardCourierCents + row.cardOnlineCents,
      0,
    ),
    cardOnlineCents: rows.reduce((sum, row) => sum + row.cardOnlineCents, 0),
    qrPayCents: rows.reduce((sum, row) => sum + row.qrPayCents, 0),
    orders: rows.reduce((sum, row) => sum + row.orders, 0),
    people: rows.length,
    disagreeing: rows.filter((row) => !row.addsUp).length,
  }
}

// ─── the summary tab's own figures ───────────────────────────────────────────

export interface FinancialSummary {
  sales?: {
    todayRevenue?: number | string
    thisMonthRevenue?: number | string
    thisMonthAvgDay?: number | string
    thisYearRevenue?: number | string
    thisMonthOrders?: number
  }
  invoices?: {
    totalCount?: number
    unpaidCount?: number
    overdueCount?: number
    unpaidTotal?: number | string
    overdueTotal?: number | string
    thisMonthTotal?: number | string
    thisYearTotal?: number | string
  }
  topSuppliers?: { supplierId?: number; name?: string; invoiceCount?: number; totalSpend?: number | string }[]
  upcomingPayments?: {
    id?: number
    invoiceNumber?: string
    supplierName?: string | null
    dueDate?: string
    daysUntilDue?: number
    totalGross?: number | string
    currency?: string
    iban?: string | null
    bucket?: string
  }[]
  /**
   * The five cash-flow figures over **every** matching invoice - §10A.321, FB-962.
   *
   * The list above is capped at twenty by the server, and `dueSoon` used to reduce it: past twenty matching
   * invoices the two amounts, the two counts and the no-IBAN warning were all short, and the last of those
   * is gated on a positive so its **absence** asserted there were none.
   *
   * Optional, because a manager served by a backend older than that entry answers no such field - see
   * `dueSoon`'s fallback, which is a short figure rather than five noughts in emerald.
   */
  upcomingTotals?: {
    overdueGross?: number | string
    overdueCount?: number
    dueSoonGross?: number | string
    dueSoonCount?: number
    overdueWithoutIban?: number
    inWindow?: number
  }
}

export type PaymentBucket = 'overdue' | 'due_soon' | 'future'

export function paymentBucket(daysUntilDue: number | undefined): PaymentBucket {
  const days = daysUntilDue ?? 0
  if (days < 0) return 'overdue'
  if (days <= 7) return 'due_soon'
  return 'future'
}

export interface DueSoon {
  overdueCents: number
  overdueCount: number
  dueSoonCents: number
  dueSoonCount: number
  /** How many of the overdue ones cannot be paid because there is no IBAN on file. */
  withoutIban: number
  /**
   * How many invoices the window really holds, against the twenty the list carries - §9.637.
   *
   * **§3.48's pattern, and the half FB-962's totals do not cover.** The figures above are now right over
   * every matching row; the *list* is still twenty, and *"there are no more"* and *"we stopped showing you"*
   * read identically. Nought when the server sends no totals, which is what makes `upcomingTruncation`
   * silent rather than guessing.
   */
  inWindow: number
}

/**
 * What is owed and how urgently - **read from the server, not added up here** (§9.637, FB-962).
 *
 * **It used to reduce `upcomingPayments`, which the server caps at twenty.** `ReportsPage` draws five
 * claims off this - two amounts, two counts and the *no IBAN, cannot go in a SEPA batch* warning - and past
 * twenty matching invoices every one of them was short. The server's own javadoc said that list was
 * *"a list rather than a total because each one is paid separately"*, and this totalled it anyway
 * (§10A.321).
 *
 * **The shape and the name are unchanged**, so `ReportsPage` and its cases are untouched above here: the
 * same five fields, read rather than computed.
 *
 * **The fallback is the old reduction, and it is not defensiveness for its own sake.** A manager served by
 * a backend older than §10A.321 gets no `upcomingTotals`, and five noughts would draw *Po termíne 0,00 EUR*
 * in **emerald** - FB-810's styled-zero class, on money the business owes. Short beats absent there, and
 * correct everywhere else.
 *
 * **The rows keep their own bucket and that is deliberate**: `paymentBucket` is re-derived from
 * `daysUntilDue` because a bucket computed at query time goes stale on a page left open, and the list's
 * colouring is what an operator reads row by row. So these cards can differ from the rows by a day on a
 * page nobody reloads, which §10A.321 argues is smaller than a figure wrong by an unbounded amount.
 */
export function dueSoon(summary: FinancialSummary | undefined): DueSoon {
  const totals = summary?.upcomingTotals
  if (totals !== undefined) {
    return {
      overdueCents: cents(totals.overdueGross),
      overdueCount: totals.overdueCount ?? 0,
      dueSoonCents: cents(totals.dueSoonGross),
      dueSoonCount: totals.dueSoonCount ?? 0,
      withoutIban: totals.overdueWithoutIban ?? 0,
      inWindow: totals.inWindow ?? 0,
    }
  }
  const payments = summary?.upcomingPayments ?? []
  const overdue = payments.filter((payment) => paymentBucket(payment.daysUntilDue) === 'overdue')
  const soon = payments.filter((payment) => paymentBucket(payment.daysUntilDue) === 'due_soon')
  return {
    overdueCents: overdue.reduce((sum, payment) => sum + cents(payment.totalGross), 0),
    overdueCount: overdue.length,
    dueSoonCents: soon.reduce((sum, payment) => sum + cents(payment.totalGross), 0),
    dueSoonCount: soon.length,
    withoutIban: overdue.filter((payment) => (payment.iban ?? '') === '').length,
    // The fallback cannot know, and saying nought is what keeps `upcomingTruncation` quiet rather than
    // claiming the list is complete.
    inWindow: 0,
  }
}

/**
 * *20 z 47 faktúr v okne* - said only when the list is short - §9.637.
 *
 * **The other half of FB-962.** Its totals make the four figures right over every matching invoice, and the
 * list beside them is still capped at twenty by the server with nothing saying so - `PlatformStatsService`'s
 * own words, from §3.48: *"a total derived from `events.size()` can never disagree with the list it came
 * from, so it could not tell a short page from a truncated one."*
 *
 * **Silent when the server sends no figure**, because a backend older than §10A.321 answers none and a note
 * built from the rows it was given would say *20 z 20* on a truncated list - the claim this exists to stop.
 */
export function upcomingTruncation(due: DueSoon, shown: number): string {
  if (due.inWindow <= shown) return ''
  return `${formatCount(shown)} ${tt('z')} ${formatCount(due.inWindow)} ${tt('faktúr v okne')}`
}

// ─── the exports ─────────────────────────────────────────────────────────────

/**
 * **FB-123.** Six exports, one hand-rolled writer, every number quoted.
 *
 * ```js
 * const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n")
 * ```
 *
 * Quoting every cell makes a spreadsheet read every amount as text, so the one thing a financial export
 * has to do — total a column — it cannot. And the lines are joined with `\n` where RFC 4180 says
 * `\r\n`. This is FB-105's defect in a second implementation, over the yearly, product, customer,
 * low-stock, waste and VAT exports.
 *
 * All six go through `@agos/shared`'s `csvDocument` now: text quoted only when it needs to be, amounts
 * unquoted with comma decimals, a byte-order mark, and CRLF.
 */
export function yearlyCsv(rows: Year[]): string {
  return csvDocument(
    [
      tt('Rok'),
      tt('Tržby (€)'),
      tt('Objednávky'),
      tt('Rozvoz (€)'),
      tt('Reštaurácia (€)'),
      tt('Odber (€)'),
      tt('Náklady (€)'),
      tt('Zisk (€)'),
      tt('Marža (%)'),
    ],
    rows.map((row) => [
      csvNumber(row.year),
      csvAmount(row.revenueCents),
      csvNumber(row.orders),
      csvAmount(row.deliveryCents),
      csvAmount(row.restaurantCents),
      csvAmount(row.pickupCents),
      csvAmount(row.costCents),
      csvAmount(row.profitCents),
      row.marginPercent === null ? '' : csvNumber(row.marginPercent, 1),
    ]),
  )
}

export function lowStockCsv(rows: Shortfall[]): string {
  return csvDocument(
    [
      tt('Položka'),
      tt('Kategória'),
      tt('Aktuálne'),
      tt('Minimum'),
      tt('Chýba'),
      tt('Jednotka'),
      tt('Dodávateľ'),
    ],
    rows.map((row) => [
      csvField(row.name),
      csvField(row.category ?? ''),
      csvNumber(row.quantity, 3),
      csvNumber(row.minimum, 3),
      csvNumber(row.deficit, 3),
      csvField(row.unit),
      csvField(row.supplier ?? ''),
    ]),
  )
}

export function wasteCsv(rows: WasteItem[]): string {
  return csvDocument(
    [tt('Položka'), tt('Jednotka'), tt('Množstvo'), tt('Hodnota (€)'), tt('Záznamov'), tt('Poznámka')],
    rows.map((row) => [
      csvField(row.name),
      csvField(row.unit),
      csvNumber(row.quantity, 3),
      csvAmount(row.costCents),
      csvNumber(row.records),
      // The export carries the caveat too, or a spreadsheet copy of it looks like a clean figure.
      csvField(
        row.quantityLooksWrong
          ? tt('množstvo je nepravdepodobné')
          : row.costUnknown
            ? tt('položka nemá nákupnú cenu')
            : '',
      ),
    ]),
  )
}

export function vatCsv(bands: VatBand[], report: VatReport | undefined): string {
  const invoices = report?.invoices ?? []
  return csvDocument(
    [
      tt('Sadzba (%)'),
      tt('Základ (€)'),
      tt('DPH (€)'),
      tt('Celkom (€)'),
      tt('Faktúr'),
      tt('Riadkov'),
    ],
    [
      ...bands.map((band) => [
        csvNumber(band.rate, 2),
        csvAmount(band.netCents),
        csvAmount(band.vatCents),
        csvAmount(band.grossCents),
        csvNumber(band.invoices),
        csvNumber(band.lines),
      ]),
      // Legacy exports the bands and the invoices as two files; one document with a total is what a
      // tax adviser is actually sent.
      [
        csvField(tt('Spolu')),
        csvAmount(vatTotals(bands).netCents),
        csvAmount(vatTotals(bands).vatCents),
        csvAmount(vatTotals(bands).grossCents),
        csvNumber(invoices.length),
        csvNumber(bands.reduce((sum, band) => sum + band.lines, 0)),
      ],
    ],
  )
}

/**
 * The wallets as a spreadsheet.
 *
 * **§9.741 - *QR Pay* is the last column**, after *Peňaženka* rather than beside the cards, so a sheet
 * somebody already built on this export by column position reads every figure where it did. And unlike
 * the screen's column it is always there, nought included: a spreadsheet's columns are its layout, and
 * one that came and went with the data would break whatever was built on it.
 */
export function walletCsv(rows: Wallet[]): string {
  return csvDocument(
    [
      tt('Zamestnanec'),
      tt('Rola'),
      tt('Objednávok'),
      tt('Hotovosť (€)'),
      tt('Karta terminál (€)'),
      tt('Karta kuriér (€)'),
      // §9.767 - the online card's one name in the header; the column keeps its position.
      `${cardPayOnlineLabel()} (€)`,
      tt('Peňaženka (€)'),
      `${qrPayLabel()} (€)`,
    ],
    rows.map((row) => [
      csvField(row.name),
      csvField(row.role),
      csvNumber(row.orders),
      csvAmount(row.cashCents),
      csvAmount(row.cardTerminalCents),
      csvAmount(row.cardCourierCents),
      csvAmount(row.cardOnlineCents),
      csvAmount(row.walletCents),
      csvAmount(row.qrPayCents),
    ]),
  )
}

export function reportFileName(kind: string, from: string, to: string): string {
  return `${kind}-${from}-${to}.csv`
}

export function daysFileName(kind: string, days: number): string {
  return `${kind}-${days}d.csv`
}

export { formatCount }
